import React, { useEffect, useMemo, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import DOMPurify from "dompurify";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { buildWorkspaceGistUrl, buildWorkspaceSubjectUrl } from "../../utils/publicUrls";
import styles from "./ImportReviewWorkspacePage.module.css";

const IMPORT_REVIEW_TREE_QUERY = gql`
  query ImportReviewTree($subjectPublicId: String!, $rootGistPublicId: String) {
    importReviewTree(subjectPublicId: $subjectPublicId, rootGistPublicId: $rootGistPublicId) {
      gistCount
      commentCount
      maxDepth
      requestedSubjectGistCount
      notice
      relocatedRoot {
        publicId
        title
        status
        visibility
        subject {
          id
          publicId
          title
          slug
          kind
          visibility
          createdAt
          updatedAt
          user {
            id
            publicId
            handle
            displayName
            name
            image
          }
        }
      }
      subject {
        id
        publicId
        title
        slug
        kind
        visibility
        createdAt
        updatedAt
        user {
          id
          publicId
          handle
          displayName
          name
          image
        }
      }
      gists {
        id
        publicId
        title
        slug
        parentId
        subjectId
        status
        visibility
        publishedAt
        createdAt
        updatedAt
        user {
          id
          publicId
          handle
          displayName
          name
          image
        }
        latestVersion {
          id
          point
          createdAt
          updatedAt
        }
        latestEdit {
          id
          body
          createdAt
          updatedAt
        }
        comments {
          id
          publicId
          comment
          parentId
          userId
          gistId
          editId
          isRemoved
          createdAt
          updatedAt
          user {
            id
            publicId
            handle
            displayName
            name
            image
          }
        }
      }
    }
  }
`;

const CREATE_LAB_GIST = gql`
  mutation CreateLabGist($input: CreateGistWithContentInput!) {
    createGistWithContent(input: $input) {
      id
      publicId
      title
      parentId
      subjectId
      status
      visibility
      createdAt
    }
  }
`;

const CREATE_LAB_COMMENT = gql`
  mutation CreateLabComment($newComment: AddCommentInput) {
    addComment(comment: $newComment) {
      id
      publicId
      comment
      parentId
      userId
      gistId
      editId
      isRemoved
      createdAt
      updatedAt
      user {
        id
        publicId
        handle
        displayName
        name
        image
      }
    }
  }
`;

type ReviewUser = {
  id: number;
  publicId: string;
  handle?: string | null;
  displayName?: string | null;
  name: string;
  image?: string | null;
};

type ReviewSubject = {
  id: number;
  publicId: string;
  title: string;
  slug?: string | null;
  kind: string;
  visibility: "PUBLIC" | "UNLISTED" | "PRIVATE";
  createdAt?: string | null;
  updatedAt?: string | null;
  user: ReviewUser;
};

type ReviewVersion = {
  id: number;
  point: string;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type ReviewEdit = {
  id: number;
  body: string;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type ReviewComment = {
  id: number;
  publicId: string;
  comment: string;
  parentId?: number | null;
  userId: number;
  gistId?: number | null;
  editId?: number | null;
  isRemoved: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
  user: ReviewUser;
};

type ReviewGist = {
  id: number;
  publicId: string;
  title: string;
  slug?: string | null;
  parentId?: number | null;
  subjectId: number;
  status: "DRAFT" | "PUBLISHED";
  visibility: "PUBLIC" | "UNLISTED" | "PRIVATE";
  publishedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  user: ReviewUser;
  latestVersion?: ReviewVersion | null;
  latestEdit?: ReviewEdit | null;
  comments: ReviewComment[];
};

type RelocatedRoot = {
  publicId: string;
  title: string;
  status: "DRAFT" | "PUBLISHED";
  visibility: "PUBLIC" | "UNLISTED" | "PRIVATE";
  subject: ReviewSubject;
};

type ReviewTreeData = {
  importReviewTree: {
    gistCount: number;
    commentCount: number;
    maxDepth: number;
    requestedSubjectGistCount: number;
    notice?: string | null;
    relocatedRoot?: RelocatedRoot | null;
    subject: ReviewSubject;
    gists: ReviewGist[];
  };
};

type ReviewTreeNode = ReviewGist & {
  children: ReviewTreeNode[];
};

type CommentNode = ReviewComment & {
  replies: CommentNode[];
};

type CreateMode = "child" | "sibling" | null;

type ExportComment = {
  publicId: string;
  parentCommentPublicId: string | null;
  comment: string;
  isRemoved: boolean;
  author: string;
  createdAt?: string | null;
  updatedAt?: string | null;
  replies: ExportComment[];
};

type ExportTreeNode = {
  publicId: string;
  title: string;
  parentPublicId: string | null;
  depth: number;
  status: ReviewGist["status"];
  visibility: ReviewGist["visibility"];
  point: string;
  bodyHtml: string;
  bodyText: string;
  comments: ExportComment[];
  children: ExportTreeNode[];
};

const formatDate = (value?: string | null): string => {
  if (!value) return "unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const stripHtml = (value: string): string =>
  value
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();

const displayName = (user: ReviewUser): string =>
  user.displayName || user.name || user.handle || "Unknown user";

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const plainTextToHtml = (value: string): string => {
  const paragraphs = value
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (paragraphs.length === 0) return "<p>Draft body.</p>";

  return paragraphs
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, "<br />")}</p>`)
    .join("\n");
};

const compareGistsByCreatedTime = (a: ReviewGist, b: ReviewGist): number => {
  const aTime = new Date(a.createdAt || "").getTime() || 0;
  const bTime = new Date(b.createdAt || "").getTime() || 0;
  if (aTime !== bTime) return aTime - bTime;
  return a.id - b.id;
};

const buildReviewForest = (gists: ReviewGist[]) => {
  const nodes = gists.map((gist) => ({ ...gist, children: [] as ReviewTreeNode[] }));
  const byId = new Map<number, ReviewTreeNode>();
  const roots: ReviewTreeNode[] = [];

  nodes.forEach((node) => byId.set(node.id, node));

  nodes.forEach((node) => {
    const parent = typeof node.parentId === "number" ? byId.get(node.parentId) : null;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNode = (node: ReviewTreeNode) => {
    node.children.sort(compareGistsByCreatedTime);
    node.children.forEach(sortNode);
  };

  roots.sort(compareGistsByCreatedTime);
  roots.forEach(sortNode);

  return { roots, byId };
};

const buildCommentTree = (comments: ReviewComment[]): CommentNode[] => {
  const nodes = comments.map((comment) => ({ ...comment, replies: [] as CommentNode[] }));
  const byId = new Map<number, CommentNode>();
  const roots: CommentNode[] = [];

  nodes.forEach((node) => byId.set(node.id, node));

  nodes.forEach((node) => {
    const parent = typeof node.parentId === "number" ? byId.get(node.parentId) : null;
    if (parent) {
      parent.replies.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
};

const countSubtreeGists = (node: ReviewTreeNode): number =>
  1 + node.children.reduce((total, child) => total + countSubtreeGists(child), 0);

const countSubtreeComments = (node: ReviewTreeNode): number =>
  node.comments.length + node.children.reduce((total, child) => total + countSubtreeComments(child), 0);

const flattenTree = (nodes: ReviewTreeNode[]): ReviewTreeNode[] => {
  const result: ReviewTreeNode[] = [];
  const visit = (node: ReviewTreeNode) => {
    result.push(node);
    node.children.forEach(visit);
  };
  nodes.forEach(visit);
  return result;
};

const collectIds = (nodes: ReviewTreeNode[]): number[] => {
  const ids: number[] = [];
  const visit = (node: ReviewTreeNode) => {
    ids.push(node.id);
    node.children.forEach(visit);
  };
  nodes.forEach(visit);
  return ids;
};

const buildExportComments = (comments: ReviewComment[]): ExportComment[] => {
  const publicIdById = new Map<number, string>();
  comments.forEach((comment) => publicIdById.set(comment.id, comment.publicId));

  const visit = (comment: CommentNode): ExportComment => ({
    publicId: comment.publicId,
    parentCommentPublicId: comment.parentId ? publicIdById.get(comment.parentId) ?? null : null,
    comment: comment.comment,
    isRemoved: comment.isRemoved,
    author: displayName(comment.user),
    createdAt: comment.createdAt,
    updatedAt: comment.updatedAt,
    replies: comment.replies.map(visit),
  });

  return buildCommentTree(comments).map(visit);
};

const buildExportTree = (nodes: ReviewTreeNode[], depth = 0, parentPublicId: string | null = null): ExportTreeNode[] =>
  nodes.map((node) => ({
    publicId: node.publicId,
    title: node.title,
    parentPublicId,
    depth,
    status: node.status,
    visibility: node.visibility,
    point: node.latestVersion?.point ?? "",
    bodyHtml: node.latestEdit?.body ?? "",
    bodyText: stripHtml(node.latestEdit?.body ?? ""),
    comments: buildExportComments(node.comments),
    children: buildExportTree(node.children, depth + 1, node.publicId),
  }));

const buildFlatExportGists = (gists: ReviewTreeNode[], byId: Map<number, ReviewTreeNode>) =>
  flattenTree(gists).map((gist) => {
    const parent = gist.parentId ? byId.get(gist.parentId) ?? null : null;
    return {
      publicId: gist.publicId,
      title: gist.title,
      slug: gist.slug ?? null,
      parentPublicId: parent?.publicId ?? null,
      subjectPublicId: null as string | null,
      status: gist.status,
      visibility: gist.visibility,
      publishedAt: gist.publishedAt ?? null,
      createdAt: gist.createdAt ?? null,
      updatedAt: gist.updatedAt ?? null,
      author: displayName(gist.user),
      point: gist.latestVersion?.point ?? "",
      bodyHtml: gist.latestEdit?.body ?? "",
      bodyText: stripHtml(gist.latestEdit?.body ?? ""),
      comments: buildExportComments(gist.comments),
    };
  });

const buildOutlineText = (nodes: ReviewTreeNode[]): string => {
  const lines: string[] = [];
  const visit = (node: ReviewTreeNode, depth: number) => {
    const indent = "  ".repeat(depth);
    lines.push(`${indent}- ${node.title} [${node.publicId}] (${node.comments.length} comments)`);
    node.children.forEach((child) => visit(child, depth + 1));
  };
  nodes.forEach((root) => visit(root, 0));
  return lines.join("\n");
};

const makeSafeFilePart = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || "gistoscope-lab";

const downloadJsonFile = (filename: string, payload: unknown) => {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const getAncestorIds = (node: ReviewTreeNode | null, byId: Map<number, ReviewTreeNode>): number[] => {
  const ids: number[] = [];
  let current = node;
  while (current?.parentId) {
    const parent = byId.get(current.parentId);
    if (!parent) break;
    ids.push(parent.id);
    current = parent;
  }
  return ids;
};

type TreeNodeButtonProps = {
  node: ReviewTreeNode;
  selectedId: number | null;
  expandedIds: Set<number>;
  onSelect: (node: ReviewTreeNode) => void;
  onToggle: (node: ReviewTreeNode) => void;
  depth?: number;
};

const TreeNodeButton: React.FC<TreeNodeButtonProps> = ({
  node,
  selectedId,
  expandedIds,
  onSelect,
  onToggle,
  depth = 0,
}) => {
  const isSelected = selectedId === node.id;
  const isExpanded = expandedIds.has(node.id);
  const subtreeGists = countSubtreeGists(node);
  const subtreeComments = countSubtreeComments(node);
  const hasChildren = node.children.length > 0;

  return (
    <li className={styles.treeNode}>
      <div className={`${styles.treeRow} ${isSelected ? styles.treeRowSelected : ""}`} style={{ paddingLeft: `${8 + depth * 20}px` }}>
        <button
          type="button"
          className={`${styles.expandButton} ${!hasChildren ? styles.expandButtonEmpty : ""}`}
          onClick={() => hasChildren && onToggle(node)}
          aria-label={hasChildren ? (isExpanded ? "Collapse branch" : "Expand branch") : "No child gists"}
          aria-expanded={hasChildren ? isExpanded : undefined}
        >
          {hasChildren ? (isExpanded ? "▾" : "▸") : "•"}
        </button>
        <button type="button" className={styles.treeButton} onClick={() => onSelect(node)}>
          <span className={styles.treeTitle}>{node.title}</span>
          <span className={styles.treeMetaLine}>
            <span>{node.children.length} child{node.children.length === 1 ? "" : "ren"}</span>
            <span>{node.comments.length} direct comment{node.comments.length === 1 ? "" : "s"}</span>
            <span>subtree: {subtreeGists}/{subtreeComments}</span>
          </span>
        </button>
      </div>
      {hasChildren && isExpanded && (
        <ol className={styles.treeListNested}>
          {node.children.map((child) => (
            <TreeNodeButton
              key={child.id}
              node={child}
              selectedId={selectedId}
              expandedIds={expandedIds}
              onSelect={onSelect}
              onToggle={onToggle}
              depth={depth + 1}
            />
          ))}
        </ol>
      )}
    </li>
  );
};

type CommentViewProps = {
  comment: CommentNode;
  depth?: number;
};

const CommentView: React.FC<CommentViewProps> = ({ comment, depth = 0 }) => {
  return (
    <li className={styles.commentNode} style={{ marginLeft: `${depth * 16}px` }}>
      <article className={styles.commentCard}>
        <div className={styles.commentMeta}>
          <strong>{displayName(comment.user)}</strong>
          <span>{formatDate(comment.createdAt)}</span>
          <span>{comment.publicId}</span>
        </div>
        <p className={comment.isRemoved ? styles.removedComment : ""}>{comment.comment}</p>
      </article>
      {comment.replies.length > 0 && (
        <ol className={styles.commentListNested}>
          {comment.replies.map((reply) => (
            <CommentView key={reply.id} comment={reply} depth={depth + 1} />
          ))}
        </ol>
      )}
    </li>
  );
};

const ImportReviewWorkspacePage: React.FC = () => {
  const { subjectPublicId } = useParams<{ subjectPublicId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const rootGistPublicId = searchParams.get("rootGistPublicId") || null;
  const selectedGistPublicId = searchParams.get("gistPublicId") || null;
  const [copiedOutline, setCopiedOutline] = useState(false);
  const [downloadedLabJson, setDownloadedLabJson] = useState(false);
  const [rootLookupValue, setRootLookupValue] = useState(rootGistPublicId ?? "");
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [hasInitializedExpansion, setHasInitializedExpansion] = useState(false);
  const [createMode, setCreateMode] = useState<CreateMode>(null);
  const [newGistTitle, setNewGistTitle] = useState("");
  const [newGistPoint, setNewGistPoint] = useState("");
  const [newGistBody, setNewGistBody] = useState("");
  const [newCommentText, setNewCommentText] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery<ReviewTreeData>(IMPORT_REVIEW_TREE_QUERY, {
    variables: {
      subjectPublicId: subjectPublicId ?? "",
      rootGistPublicId,
    },
    skip: !subjectPublicId,
    fetchPolicy: "cache-and-network",
  });

  const [createLabGist, { loading: creatingGist }] = useMutation(CREATE_LAB_GIST);
  const [createLabComment, { loading: creatingComment }] = useMutation(CREATE_LAB_COMMENT);

  const gists = useMemo(
    () => data?.importReviewTree.gists ?? [],
    [data?.importReviewTree.gists],
  );
  const forest = useMemo(() => buildReviewForest(gists), [gists]);
  const flatGists = useMemo(() => flattenTree(forest.roots), [forest.roots]);
  const selectedGist = useMemo(() => {
    if (selectedGistPublicId) {
      return flatGists.find((gist) => gist.publicId === selectedGistPublicId) ?? flatGists[0] ?? null;
    }
    return flatGists[0] ?? null;
  }, [flatGists, selectedGistPublicId]);

  useEffect(() => {
    if (!flatGists.length || hasInitializedExpansion) return;
    setExpandedIds(new Set(collectIds(forest.roots)));
    setHasInitializedExpansion(true);
  }, [flatGists.length, forest.roots, hasInitializedExpansion]);

  useEffect(() => {
    if (!selectedGist || selectedGistPublicId === selectedGist.publicId) return;
    const next = new URLSearchParams(searchParams);
    next.set("gistPublicId", selectedGist.publicId);
    setSearchParams(next, { replace: true });
  }, [selectedGist, selectedGistPublicId, searchParams, setSearchParams]);

  useEffect(() => {
    if (!selectedGist) return;
    const ancestors = getAncestorIds(selectedGist, forest.byId);
    if (ancestors.every((id) => expandedIds.has(id))) return;
    setExpandedIds((previous) => {
      const next = new Set(previous);
      ancestors.forEach((id) => next.add(id));
      return next;
    });
  }, [selectedGist, forest.byId, expandedIds]);

  const commentTree = useMemo(
    () => buildCommentTree(selectedGist?.comments ?? []),
    [selectedGist?.comments],
  );

  const parentGist = selectedGist?.parentId ? forest.byId.get(selectedGist.parentId) ?? null : null;
  const siblingGists = selectedGist
    ? (parentGist ? parentGist.children : forest.roots).filter((gist) => gist.id !== selectedGist.id)
    : [];
  const childGists = selectedGist?.children ?? [];

  const handleSelectGist = (gist: ReviewTreeNode) => {
    const next = new URLSearchParams(searchParams);
    next.set("gistPublicId", gist.publicId);
    setSearchParams(next);
    setActionError(null);
    setActionMessage(null);
  };

  const toggleNode = (node: ReviewTreeNode) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (next.has(node.id)) {
        next.delete(node.id);
      } else {
        next.add(node.id);
      }
      return next;
    });
  };

  const expandAll = () => setExpandedIds(new Set(collectIds(forest.roots)));
  const collapseToRoots = () => setExpandedIds(new Set(forest.roots.map((root) => root.id)));

  const handleRootLookupSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedRootPublicId = rootLookupValue.trim();
    const next = new URLSearchParams(searchParams);

    if (trimmedRootPublicId) {
      next.set("rootGistPublicId", trimmedRootPublicId);
    } else {
      next.delete("rootGistPublicId");
    }

    next.delete("gistPublicId");
    setHasInitializedExpansion(false);
    setSearchParams(next);
  };

  const clearRootLookup = () => {
    setRootLookupValue("");
    const next = new URLSearchParams(searchParams);
    next.delete("rootGistPublicId");
    next.delete("gistPublicId");
    setHasInitializedExpansion(false);
    setSearchParams(next);
  };

  const copyOutline = async () => {
    const outline = buildOutlineText(forest.roots);

    try {
      await window.navigator.clipboard.writeText(outline);
      setCopiedOutline(true);
      window.setTimeout(() => setCopiedOutline(false), 2200);
    } catch {
      window.prompt("Copy import review outline:", outline);
    }
  };

  const downloadLabJson = () => {
    const exportedAt = new Date().toISOString();
    const flatGistExports = buildFlatExportGists(forest.roots, forest.byId).map((gist) => ({
      ...gist,
      subjectPublicId: subject?.publicId ?? null,
    }));
    const payload = {
      format: "gistoscope-lab-export.v1",
      exportedAt,
      exportPurpose:
        "Portable snapshot for model review, local backup, and editorial discussion. This file is read-only and does not publish content.",
      subject: (() => {
        const currentSubject = data?.importReviewTree.subject;
        return currentSubject
          ? {
              publicId: currentSubject.publicId,
              title: currentSubject.title,
              slug: currentSubject.slug ?? null,
              kind: currentSubject.kind,
              visibility: currentSubject.visibility,
              owner: displayName(currentSubject.user),
              createdAt: currentSubject.createdAt ?? null,
              updatedAt: currentSubject.updatedAt ?? null,
            }
          : null;
      })(),
      rootGistPublicId: rootGistPublicId || forest.roots[0]?.publicId || null,
      selectedGistPublicId: selectedGist?.publicId ?? null,
      stats: {
        gistCount: data?.importReviewTree.gistCount ?? flatGists.length,
        commentCount: data?.importReviewTree.commentCount ?? flatGistExports.reduce((total, gist) => total + gist.comments.length, 0),
        maxDepth: data?.importReviewTree.maxDepth ?? 0,
      },
      outlineMarkdown: buildOutlineText(forest.roots),
      tree: buildExportTree(forest.roots),
      gists: flatGistExports,
    };

    const currentSubject = data?.importReviewTree.subject;
    const filename = `${makeSafeFilePart(currentSubject?.publicId || currentSubject?.title || "gistoscope-lab")}-export-${exportedAt.slice(0, 10)}.json`;
    downloadJsonFile(filename, payload);
    setDownloadedLabJson(true);
    window.setTimeout(() => setDownloadedLabJson(false), 2600);
  };

  const openCreateForm = (mode: Exclude<CreateMode, null>) => {
    setCreateMode(mode);
    setNewGistTitle("");
    setNewGistPoint("");
    setNewGistBody("");
    setActionError(null);
    setActionMessage(null);
  };

  const createGistInLab = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedGist || !createMode) return;

    const title = newGistTitle.trim();
    const point = newGistPoint.trim();
    const body = newGistBody.trim();

    if (!title || !point || !body) {
      setActionError("Title, point, and body are required.");
      return;
    }

    const parentId = createMode === "child" ? selectedGist.id : selectedGist.parentId ?? null;

    try {
      setActionError(null);
      const result = await createLabGist({
        variables: {
          input: {
            title,
            point,
            body: plainTextToHtml(body),
            subjectId: selectedGist.subjectId,
            parentId,
            status: "DRAFT",
            visibility: "PRIVATE",
          },
        },
      });

      const createdPublicId = result.data?.createGistWithContent?.publicId;
      setCreateMode(null);
      setNewGistTitle("");
      setNewGistPoint("");
      setNewGistBody("");
      await refetch();

      if (createdPublicId) {
        const next = new URLSearchParams(searchParams);
        next.set("gistPublicId", createdPublicId);
        setSearchParams(next);
      }

      setActionMessage(createMode === "child" ? "Child gist created inside this lab." : "Sibling gist created inside this lab.");
    } catch (err: any) {
      setActionError(err?.message || "Could not create gist inside the lab.");
    }
  };

  const createCommentInLab = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedGist) return;

    const comment = newCommentText.trim();
    if (!comment) {
      setActionError("Comment text is required.");
      return;
    }

    try {
      setActionError(null);
      await createLabComment({
        variables: {
          newComment: {
            comment,
            gistId: selectedGist.id,
            editId: selectedGist.latestEdit?.id ?? undefined,
          },
        },
      });
      setNewCommentText("");
      await refetch();
      setActionMessage("Comment added to the selected gist inside this lab.");
    } catch (err: any) {
      setActionError(err?.message || "Could not create comment inside the lab.");
    }
  };

  if (loading && !data) {
    return (
      <main className={styles.page}>
        <section className={styles.stateCard}>Loading lab workspace…</section>
      </main>
    );
  }

  if (error) {
    return (
      <main className={styles.page}>
        <section className={styles.stateCard}>
          <p className={styles.eyebrow}>Gistoscope Lab Workspace</p>
          <h1>Lab workspace could not be loaded</h1>
          <p>{error.message}</p>
        </section>
      </main>
    );
  }

  const reviewTree = data?.importReviewTree;
  const subject = reviewTree?.subject;

  if (!reviewTree || !subject) {
    return (
      <main className={styles.page}>
        <section className={styles.stateCard}>Lab workspace data is not available.</section>
      </main>
    );
  }

  const bodyHtml = selectedGist?.latestEdit?.body || "";
  const safeBodyHtml = DOMPurify.sanitize(bodyHtml || "<p>No body text yet.</p>");
  const relocatedRoot = reviewTree.relocatedRoot ?? null;
  const hasNoGistsInRequestedSubject = reviewTree.requestedSubjectGistCount === 0;
  const selectedDepth = selectedGist ? getAncestorIds(selectedGist, forest.byId).length + 1 : 0;

  return (
    <main className={styles.page}>
      <header className={styles.hero}>
        <div>
          <p className={styles.eyebrow}>Private Gistoscope Laboratory</p>
          <h1>{subject.title}</h1>
          <p className={styles.heroText}>
            This is a bounded lab space. All lab work stays inside one private subject unless you explicitly open an external link.
            Use it as a micro-Gistoscope for reviewing, editing, and growing one imported white paper tree.
          </p>
        </div>
        <div className={styles.heroActions}>
          <Link className={styles.secondaryAction} to={buildWorkspaceSubjectUrl(subject)}>
            Open subject workspace
          </Link>
          <Link className={styles.secondaryAction} to="/workspace/drafts">
            My Drafts
          </Link>
          <button className={styles.secondaryAction} type="button" onClick={copyOutline}>
            {copiedOutline ? "Copied outline" : "Copy outline"}
          </button>
          <button className={styles.primaryAction} type="button" onClick={downloadLabJson}>
            {downloadedLabJson ? "Downloaded JSON" : "Download lab JSON"}
          </button>
        </div>
        <div className={styles.summaryRow}>
          <span>{subject.visibility.toLowerCase()} lab subject</span>
          <span>{reviewTree.gistCount} gist{reviewTree.gistCount === 1 ? "" : "s"}</span>
          <span>{reviewTree.commentCount} comment{reviewTree.commentCount === 1 ? "" : "s"}</span>
          <span>{reviewTree.maxDepth} level{reviewTree.maxDepth === 1 ? "" : "s"}</span>
          <span>Owner: {displayName(subject.user)}</span>
        </div>
      </header>

      <section className={styles.labBoundaryCard}>
        <div>
          <p className={styles.eyebrow}>Lab Boundary</p>
          <h2>One subject. One tree. No accidental wandering.</h2>
          <p>
            New child/sibling gists created here are saved as <strong>DRAFT + PRIVATE</strong> inside this same subject.
            Links to other parts of Gistoscope can be opened, but they leave this laboratory context.
          </p>
        </div>
        <div className={styles.labBoundaryStats}>
          <span>Subject: {subject.publicId}</span>
          <span>Root: {rootGistPublicId || forest.roots[0]?.publicId || "not selected"}</span>
        </div>
      </section>

      {(reviewTree.notice || relocatedRoot || hasNoGistsInRequestedSubject) && (
        <section className={styles.diagnosticCard}>
          <p className={styles.eyebrow}>Review Locator</p>
          {reviewTree.notice && <h2>{reviewTree.notice}</h2>}

          {relocatedRoot ? (
            <div className={styles.relocatedBox}>
              <p>
                The root gist <strong>{relocatedRoot.title}</strong> is currently attached to another subject.
                Open that subject to review the live tree.
              </p>
              <Link
                className={styles.primaryAction}
                to={`/workspace/import-review/${encodeURIComponent(relocatedRoot.subject.publicId)}?rootGistPublicId=${encodeURIComponent(relocatedRoot.publicId)}`}
              >
                Open current subject: {relocatedRoot.subject.title}
              </Link>
            </div>
          ) : null}

          {hasNoGistsInRequestedSubject && !relocatedRoot ? (
            <form className={styles.rootLookupForm} onSubmit={handleRootLookupSubmit}>
              <label htmlFor="import-review-root-lookup">Find a moved/promoted tree by root gist publicId</label>
              <div className={styles.rootLookupControls}>
                <input
                  id="import-review-root-lookup"
                  value={rootLookupValue}
                  onChange={(event) => setRootLookupValue(event.target.value)}
                  placeholder="Example: g_mvp_test_wp_001_root"
                />
                <button className={styles.primaryAction} type="submit">
                  Locate tree
                </button>
                {rootGistPublicId && (
                  <button className={styles.secondaryAction} type="button" onClick={clearRootLookup}>
                    Clear root
                  </button>
                )}
              </div>
              <p className={styles.emptyText}>
                This is useful after promotion, because promotion moves the same Gists to the final subject instead of copying them.
              </p>
            </form>
          ) : null}
        </section>
      )}

      <section className={styles.workspaceGrid}>
        <aside className={styles.treePanel}>
          <div className={styles.panelHeader}>
            <p className={styles.eyebrow}>Lab Tree</p>
            <h2>Structure map</h2>
            <p className={styles.emptyText}>This is the canonical map for this laboratory subject.</p>
          </div>
          <div className={styles.treeControls}>
            <button type="button" className={styles.tinyAction} onClick={expandAll}>Expand all</button>
            <button type="button" className={styles.tinyAction} onClick={collapseToRoots}>Collapse to roots</button>
            <button type="button" className={styles.tinyAction} onClick={() => refetch()}>Refresh</button>
          </div>
          {forest.roots.length === 0 ? (
            <div className={styles.emptyTreeBox}>
              <p className={styles.emptyText}>No gists were found in this lab subject.</p>
              <p className={styles.emptyText}>
                If you already promoted this import, use the locator above or open the promoted subject directly.
              </p>
            </div>
          ) : (
            <ol className={styles.treeList}>
              {forest.roots.map((root) => (
                <TreeNodeButton
                  key={root.id}
                  node={root}
                  selectedId={selectedGist?.id ?? null}
                  expandedIds={expandedIds}
                  onSelect={handleSelectGist}
                  onToggle={toggleNode}
                />
              ))}
            </ol>
          )}
        </aside>

        <section className={styles.readerPanel}>
          {actionError && <div className={styles.errorBanner}>{actionError}</div>}
          {actionMessage && <div className={styles.successBanner}>{actionMessage}</div>}

          {selectedGist ? (
            <article className={styles.readerCard}>
              <div className={styles.gistHeader}>
                <div>
                  <p className={styles.eyebrow}>Selected Gist</p>
                  <h2>{selectedGist.title}</h2>
                  <p className={styles.gistMeta}>
                    depth {selectedDepth} · {selectedGist.status.toLowerCase()} · {selectedGist.visibility.toLowerCase()} · updated {formatDate(selectedGist.updatedAt)} · {selectedGist.publicId}
                  </p>
                </div>
                <div className={styles.gistActions}>
                  <Link className={styles.primaryAction} to={buildWorkspaceGistUrl(selectedGist, "read")}>
                    Open read view
                  </Link>
                  <Link className={styles.secondaryAction} to={buildWorkspaceGistUrl(selectedGist, "advanced")}>
                    Continue writing
                  </Link>
                </div>
              </div>

              <section className={styles.contextGrid}>
                <div>
                  <p className={styles.contextLabel}>Parent</p>
                  {parentGist ? (
                    <button type="button" className={styles.contextLink} onClick={() => handleSelectGist(parentGist)}>
                      ↑ {parentGist.title}
                    </button>
                  ) : (
                    <span className={styles.contextMuted}>This is a root gist.</span>
                  )}
                </div>
                <div>
                  <p className={styles.contextLabel}>Children</p>
                  {childGists.length > 0 ? (
                    <div className={styles.contextLinks}>{childGists.map((child) => (
                      <button key={child.id} type="button" className={styles.contextLink} onClick={() => handleSelectGist(child)}>
                        ↓ {child.title}
                      </button>
                    ))}</div>
                  ) : (
                    <span className={styles.contextMuted}>No child gists.</span>
                  )}
                </div>
                <div>
                  <p className={styles.contextLabel}>Siblings</p>
                  {siblingGists.length > 0 ? (
                    <div className={styles.contextLinks}>{siblingGists.map((sibling) => (
                      <button key={sibling.id} type="button" className={styles.contextLink} onClick={() => handleSelectGist(sibling)}>
                        ↔ {sibling.title}
                      </button>
                    ))}</div>
                  ) : (
                    <span className={styles.contextMuted}>No siblings at this level.</span>
                  )}
                </div>
              </section>

              <section className={styles.labActionsCard}>
                <div>
                  <p className={styles.eyebrow}>Grow this lab tree</p>
                  <h3>Add structure inside the same private subject</h3>
                </div>
                <div className={styles.gistActions}>
                  <button type="button" className={styles.secondaryAction} onClick={() => openCreateForm("child")}>
                    Add child gist
                  </button>
                  <button type="button" className={styles.secondaryAction} onClick={() => openCreateForm("sibling")}>
                    Add sibling gist
                  </button>
                </div>
              </section>

              {createMode && (
                <form className={styles.createForm} onSubmit={createGistInLab}>
                  <p className={styles.eyebrow}>{createMode === "child" ? "New child gist" : "New sibling gist"}</p>
                  <label>
                    Title
                    <input value={newGistTitle} onChange={(event) => setNewGistTitle(event.target.value)} placeholder="New gist title" />
                  </label>
                  <label>
                    Point
                    <textarea value={newGistPoint} onChange={(event) => setNewGistPoint(event.target.value)} rows={2} placeholder="Short point / summary" />
                  </label>
                  <label>
                    Body
                    <textarea value={newGistBody} onChange={(event) => setNewGistBody(event.target.value)} rows={7} placeholder="Draft body. You can polish it later in Continue writing." />
                  </label>
                  <div className={styles.formActions}>
                    <button className={styles.primaryAction} type="submit" disabled={creatingGist}>
                      {creatingGist ? "Creating…" : "Create private draft gist"}
                    </button>
                    <button className={styles.secondaryAction} type="button" onClick={() => setCreateMode(null)}>
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              {selectedGist.latestVersion?.point && (
                <section className={styles.pointBox}>
                  <p className={styles.eyebrow}>Point</p>
                  <p>{selectedGist.latestVersion.point}</p>
                </section>
              )}

              <section className={styles.bodyBox}>
                <div dangerouslySetInnerHTML={{ __html: safeBodyHtml }} />
              </section>
            </article>
          ) : (
            <div className={styles.stateCard}>Choose a gist in the tree.</div>
          )}
        </section>

        <aside className={styles.commentsPanel}>
          <div className={styles.panelHeader}>
            <p className={styles.eyebrow}>Selected Gist Comments</p>
            <h2>{selectedGist ? selectedGist.comments.length : 0} comment{selectedGist?.comments.length === 1 ? "" : "s"}</h2>
            {selectedGist && <p className={styles.emptyText}>Attached to: {selectedGist.title}</p>}
          </div>

          {selectedGist && (
            <form className={styles.commentForm} onSubmit={createCommentInLab}>
              <label htmlFor="new-lab-comment">Add comment inside this lab</label>
              <textarea
                id="new-lab-comment"
                value={newCommentText}
                onChange={(event) => setNewCommentText(event.target.value)}
                rows={4}
                placeholder="Add a note, caveat, objection, source note, or editorial reminder."
              />
              <button className={styles.primaryAction} type="submit" disabled={creatingComment}>
                {creatingComment ? "Adding…" : "Add comment"}
              </button>
            </form>
          )}

          {!selectedGist || commentTree.length === 0 ? (
            <p className={styles.emptyText}>No comments are attached to this gist yet.</p>
          ) : (
            <ol className={styles.commentList}>
              {commentTree.map((comment) => (
                <CommentView key={comment.id} comment={comment} />
              ))}
            </ol>
          )}
        </aside>
      </section>
    </main>
  );
};

export default ImportReviewWorkspacePage;
